import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

interface Signature {
  Args: {
    readonly: boolean;
    tags: string[];
    onUpdate: (tags: string[]) => void;
    icon?: string | ((tag: string) => string);
    tagClass?: string | ((tag: string) => string);
    tagText?: (tag: string) => string;
    addLabel?: string;
    emptyLabel?: string;
  };
}

export class Tags extends Component<Signature> {
  iconFor = (tag: string) => {
    if (typeof this.args.icon === 'function') {
      return this.args.icon(tag);
    }
    return this.args.icon ?? 'ph:tag';
  };

  tagClassFor = (tag: string) => {
    if (typeof this.args.tagClass === 'function') {
      return this.args.tagClass(tag);
    }
    return this.args.tagClass ?? 'text-bg-light';
  };

  tagTextFor = (tag: string) => {
    if (typeof this.args.tagText === 'function') {
      return this.args.tagText(tag);
    }
    return tag;
  };

  get addLabel() {
    return this.args.addLabel ?? 'Add an item';
  }

  get emptyLabel() {
    return this.args.emptyLabel ?? 'No items have been added';
  }

  removeTag = (tag: string) => {
    this.args.onUpdate(this.args.tags.filter((t) => t !== tag));
  };

  addTag = (event: Event) => {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const tag = input.value.trim();
    if (tag && !(this.args.tags ?? []).includes(tag)) {
      this.args.onUpdate([...(this.args.tags ?? []), tag]);
      input.value = '';
    }
  };

  handleAddTagKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.addTag(event);
    }
  };

  <template>
    {{#if @readonly}}
      <div class="form-control bg-body-secondary" ...attributes>
        {{#if @tags.length}}
          {{#each @tags as |tag|}}
            <span
              class="badge rounded-pill fs-6 border border-dark border-1 my-1
                {{this.tagClassFor tag}}"
            >
              <iconify-icon inline icon={{this.iconFor tag}} />
              {{this.tagTextFor tag}}
              <iconify-icon inline icon="ph:x" class="invisible" />
            </span>
          {{/each}}
        {{else}}
          <span class="text-muted">{{this.emptyLabel}}</span>
        {{/if}}
      </div>
    {{else}}
      <div class="form-control" ...attributes>
        {{#each @tags as |tag|}}
          <button
            type="button"
            class="badge rounded-pill fs-6 border-1 my-1
              {{this.tagClassFor tag}}"
            title="Remove '{{tag}}'"
            {{on "click" (fn this.removeTag tag)}}
          >
            <iconify-icon inline icon={{this.iconFor tag}} />
            {{this.tagTextFor tag}}
            <iconify-icon inline icon="ph:x" />
          </button>
        {{/each}}
        <input
          type="text"
          class="d-inline-block rounded-pill fs-6 border-1 text-bg-white border-dark px-3 my-1"
          placeholder={{this.addLabel}}
          aria-label={{this.addLabel}}
          {{on "change" this.addTag}}
          {{on "keydown" this.handleAddTagKeydown}}
        />
      </div>
    {{/if}}
  </template>
}
